import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MailCheck, ArrowRight, X, RotateCw } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../ui/dialog";
import api from "../../lib/api";

/**
 * VerifyEmailRequiredModal — shown when a logged-in user tries to perform
 * an action that requires a verified email (subscription checkout, adding
 * a wallet, etc.).
 *
 * Server returns 412 { error: "email_verification_required", action,
 * email, message } on:
 *   • POST /billing/checkout    — gating subscription pay
 *   • POST /users/me/wallets    — gating new wallet address
 *
 * Frontend api.js interceptor catches the 412 and dispatches a
 * `cryptpe:verify-email` event. Dashboard mounts the listener.
 *
 * Modal copy:
 *   • Tells the user *what* they were about to do ("upgrading your plan",
 *     "adding a wallet") so the gate feels intentional, not arbitrary.
 *   • One-click "resend verification email" — no leaving the page, no
 *     hunting for the original email.
 */
export default function VerifyEmailRequiredModal({ open, payload, onClose }) {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const handleResend = async () => {
    setBusy(true);
    try {
      await api.post("/auth/send-verification");
      setSent(true);
      toast.success("Verification email sent. Check your inbox.");
    } catch (_) {
      toast.error("Could not send verification email. Try again in a minute.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const action = payload?.action || "this action";
  const email = payload?.email || "";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        data-testid="verify-email-required-modal"
        className="max-w-md bg-zinc-950 border border-zinc-800 text-zinc-100"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="close"
          data-testid="verify-email-modal-close"
          className="absolute top-3 right-3 text-zinc-500 hover:text-zinc-200 transition-colors"
        >
          <X size={18} />
        </button>

        <DialogHeader>
          <div className="inline-flex items-center gap-2 mono text-[10px] uppercase tracking-[0.15em] text-white">
            <MailCheck size={14} /> verify your email
          </div>
          <DialogTitle className="text-2xl font-bold tracking-tight mt-2">
            Confirm your email first
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-zinc-300 leading-relaxed mt-1">
          We need a verified email before {action}. This keeps your account
          recoverable and makes sure receipts + alerts reach you.
        </p>

        {email && (
          <div className="mt-3 mono text-[11px] text-zinc-500 bg-zinc-900/40 border border-zinc-800 rounded-md p-2.5">
            sending verification to <span className="text-zinc-200">{email}</span>
          </div>
        )}

        <div className="mt-5 flex items-center gap-3">
          <button
            type="button"
            data-testid="verify-email-resend-btn"
            onClick={handleResend}
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white text-black font-semibold text-sm disabled:opacity-60 disabled:cursor-not-allowed hover:bg-neutral-200 transition-colors"
          >
            {busy ? <RotateCw size={14} className="animate-spin" /> : <ArrowRight size={14} />}
            {sent ? "resend again" : "resend verification email"}
          </button>
          <Link
            to="/dashboard?tab=settings"
            data-testid="verify-email-settings-link"
            onClick={onClose}
            className="mono text-[11px] text-zinc-400 hover:text-zinc-200"
          >
            change email →
          </Link>
        </div>

        {sent && (
          <div
            data-testid="verify-email-sent-confirmation"
            className="mt-4 mono text-[11px] text-white border border-white/20 bg-white/[0.04] rounded-md p-2.5"
          >
            check your inbox — the link is valid for 24 hours
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
